import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { databaseService } from '../../services/database';
import { insightsService } from '../../services/insights';
import { SceneStat } from '../../types/insights';

// ---------------------------------------------------------------------------
// ScenesTab — the per-scene x version correlation grid
// (telemetry.scene_matrix_report). One fetch WITHOUT a version filter, so the
// distinct versions in the window populate the A/B selects; version A/B
// renders two heatmap tables side by side.
// ---------------------------------------------------------------------------

interface ScenesTabProps {
  roomId: string;
}

const card: CSSProperties = {
  background: '#fff',
  border: '1px solid #e4e9f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#54626f',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const selectStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d5dce5',
  borderRadius: 8,
  fontSize: 13,
};

interface ColDef {
  key: keyof SceneStat;
  label: string;
  digits: number;
  /** 'bad' = red shading (more is worse), 'good' = blue shading. */
  tone: 'bad' | 'good' | 'neutral';
}

const COLS: ColDef[] = [
  { key: 'visits', label: 'visits', digits: 0, tone: 'neutral' },
  { key: 'play_seconds', label: 'play s', digits: 0, tone: 'neutral' },
  { key: 'fps_avg', label: 'fps', digits: 1, tone: 'good' },
  { key: 'fps_p1_avg', label: 'fps p1', digits: 1, tone: 'good' },
  { key: 'hitch_per_min', label: 'hitch/min', digits: 2, tone: 'bad' },
  { key: 'bitrate_avg', label: 'bitrate', digits: 0, tone: 'good' },
  { key: 'transmit_avg', label: 'tx ms', digits: 1, tone: 'bad' },
  { key: 'transmit_max', label: 'tx max', digits: 0, tone: 'bad' },
  { key: 'net_sent_avg', label: 'net', digits: 0, tone: 'neutral' },
  { key: 'gaps', label: 'gaps', digits: 0, tone: 'bad' },
  { key: 'disconnects', label: 'disc', digits: 0, tone: 'bad' },
  { key: 'disconnects_per_hour', label: 'disc/h', digits: 2, tone: 'bad' },
  { key: 'screendoor_suspects', label: 'scrn', digits: 0, tone: 'bad' },
];

function heatBg(value: number | null, max: number, tone: ColDef['tone']): string | undefined {
  if (value == null || max <= 0 || tone === 'neutral') return undefined;
  const alpha = Math.min(Math.abs(value) / max, 1) * 0.32;
  if (alpha < 0.02) return undefined;
  return tone === 'bad' ? `rgba(192,57,43,${alpha})` : `rgba(43,106,192,${alpha})`;
}

function MatrixTable({ rows, title }: { rows: SceneStat[]; title?: string }) {
  const colMax = useMemo(() => {
    const max: Record<string, number> = {};
    for (const c of COLS) {
      max[c.key] = Math.max(0, ...rows.map((r) => Math.abs((r[c.key] as number | null) ?? 0)));
    }
    return max;
  }, [rows]);

  const thTd = (head = false, left = false): CSSProperties => ({
    textAlign: left ? 'left' : 'right',
    padding: '6px 8px',
    borderBottom: '1px solid #eef1f5',
    whiteSpace: 'nowrap',
    fontVariant: 'tabular-nums',
    color: head ? '#8a97a6' : '#16202e',
    fontWeight: head ? 600 : 400,
    fontSize: head ? 11 : 12,
    textTransform: head ? 'uppercase' : 'none',
  });

  return (
    <div style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}>
      {title && (
        <div style={{ fontSize: 13, fontWeight: 650, marginBottom: 6, color: '#16202e' }}>{title}</div>
      )}
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={thTd(true, true)}>Scene</th>
            {COLS.map((c) => (
              <th key={c.key} style={thTd(true)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.scene_index}-${r.experience_version}-${i}`}>
              <td style={thTd(false, true)}>
                {r.scene_name ?? (r.scene_index != null ? `Scene ${r.scene_index}` : '—')}
              </td>
              {COLS.map((c) => {
                const v = r[c.key] as number | null;
                return (
                  <td
                    key={c.key}
                    style={{ ...thTd(), background: heatBg(v, colMax[c.key], c.tone) }}
                  >
                    {v == null ? '—' : v.toFixed(c.digits)}
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={COLS.length + 1} style={{ ...thTd(false, true), color: '#8a97a6' }}>
                No scene data in this window.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function ScenesTab({ roomId }: ScenesTabProps) {
  const [experiences, setExperiences] = useState<{ id: string; name: string }[]>([]);
  const [experienceName, setExperienceName] = useState('');
  const [days, setDays] = useState(30);
  const [excludeTests, setExcludeTests] = useState(true);
  const [rows, setRows] = useState<SceneStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');

  useEffect(() => {
    databaseService.listExperiences().then((list) => {
      setExperiences(list as any[]);
      if (list.length > 0) {
        setExperienceName((prev) => prev || (list[0] as any).name);
      }
    });
  }, []);

  useEffect(() => {
    if (!experienceName) return;
    let cancelled = false;
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400000);
    insightsService
      .getSceneMatrix({
        experienceName,
        experienceVersion: null, // all versions — A/B filtered client-side
        roomId,
        fromIso: from.toISOString(),
        toIso: to.toISOString(),
        excludeTests,
      })
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, experienceName, days, excludeTests]);

  const versions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.experience_version) set.add(r.experience_version);
    return Array.from(set).sort().reverse();
  }, [rows]);

  // Keep selections valid when the version list changes.
  useEffect(() => {
    if (versionA && !versions.includes(versionA)) setVersionA('');
    if (versionB && !versions.includes(versionB)) setVersionB('');
  }, [versions, versionA, versionB]);

  const rowsA = versionA ? rows.filter((r) => r.experience_version === versionA) : rows;
  const rowsB = versionB ? rows.filter((r) => r.experience_version === versionB) : null;

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Experience</label>
          <select style={selectStyle} value={experienceName} onChange={(e) => setExperienceName(e.target.value)}>
            {experiences.map((e) => (
              <option key={e.id} value={e.name}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Window</label>
          <select style={selectStyle} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
            {[7, 14, 30, 90].map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Version A</label>
          <select style={selectStyle} value={versionA} onChange={(e) => setVersionA(e.target.value)}>
            <option value="">All versions</option>
            {versions.map((v) => (
              <option key={v} value={v}>
                v{v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Version B (compare)</label>
          <select style={selectStyle} value={versionB} onChange={(e) => setVersionB(e.target.value)}>
            <option value="">—</option>
            {versions.map((v) => (
              <option key={v} value={v}>
                v{v}
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 8 }}>
          <input type="checkbox" checked={excludeTests} onChange={(e) => setExcludeTests(e.target.checked)} />
          Exclude test runs
        </label>
      </div>

      {loading ? (
        <div style={{ padding: '18px 0', color: '#8a97a6' }}>Loading scene matrix…</div>
      ) : rowsB ? (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <MatrixTable rows={rowsA} title={versionA ? `Version A — v${versionA}` : 'Version A — all'} />
          <MatrixTable rows={rowsB} title={`Version B — v${versionB}`} />
        </div>
      ) : (
        <MatrixTable rows={rowsA} />
      )}

      <div style={{ fontSize: 11, color: '#8a97a6', marginTop: 10 }}>
        disc = confirmed disconnects (telemetry gap + router deauth match) · gaps = all mid-run telemetry
        gaps · scrn = screendoor suspects. Red shading scales with how bad the cell is within its column.
      </div>
    </div>
  );
}
